import { useEffect } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import logoImage from "@assets/ftc-logo-light-bg.png";

export default function Login() {
  const [, setLocation] = useLocation();
  const searchParams = new URLSearchParams(window.location.search);
  const error = searchParams.get("error");

  const { data: authStatus } = useQuery({
    queryKey: ["auth-status"],
    queryFn: async () => {
      const res = await fetch("/api/auth/status");
      return res.json();
    },
  });

  useEffect(() => {
    if (authStatus?.authenticated) {
      setLocation("/");
    }
  }, [authStatus?.authenticated, setLocation]);

  if (authStatus?.authenticated) {
    return null;
  }

  const getErrorMessage = (error: string) => {
    switch (error) {
      case "not_configured":
        return "Microsoft SSO is not configured. Please contact your administrator.";
      case "not_provisioned":
        return "Your account has not been set up. Please contact your administrator to request access.";
      case "inactive":
        return "Your account has been deactivated. Please contact your administrator.";
      case "auth_failed":
        return "Authentication failed. Please try again.";
      case "no_code":
        return "Invalid login response. Please try again.";
      case "invalid_state":
        return "This sign-in attempt expired or could not be verified. Please sign in again.";
      default:
        return "An error occurred during login. Please try again.";
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src={logoImage} 
              alt="First Turn Capital" 
              className="h-16 w-auto"
            />
          </div>
          <CardDescription>Sign in to access the CRM</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{getErrorMessage(error)}</AlertDescription>
            </Alert>
          )}

          {authStatus?.ssoConfigured ? (
            <Button
              data-testid="button-microsoft-login"
              className="w-full h-12 bg-[#0078d4] hover:bg-[#106ebe] text-white"
              onClick={() => window.location.href = "/auth/login"}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21" fill="currentColor">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              Sign in with Microsoft
            </Button>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Microsoft SSO is not yet configured. Please contact your administrator to set up authentication.
              </AlertDescription>
            </Alert>
          )}

          <p className="text-xs text-center text-muted-foreground mt-4">
            Your account must be set up by an administrator before you can sign in.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
